import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";
import type { AskUserQuestionItem } from "../hooks/use-intelligent-inventory";
import * as styles from "./ask-user-question-card.module.css";
import { StreamingText } from "./streaming-text";

const OTHER_OPTION_ID = "__other__";

const recommendedBadge = (
  <RedoBadge color="orange" size="xs" text="Suggested" />
);

export const AskUserQuestionCard = memo(function AskUserQuestionCard({
  item,
  onRespond,
  disabled,
}: {
  item: AskUserQuestionItem;
  onRespond: (toolCallId: string, result: unknown) => void;
  disabled: boolean;
}) {
  const { input, toolCallId, responded, response } = item;
  const [selectedOptions, setSelectedOptions] = useState<string[]>(() => {
    if (!response) {
      return [];
    }
    const options = [...(response.selectedOptions ?? [])];
    if (response.otherText) {
      options.push(OTHER_OPTION_ID);
    }
    return options;
  });
  const [otherText, setOtherText] = useState(response?.otherText ?? "");
  const isOtherSelected = selectedOptions.includes(OTHER_OPTION_ID);
  const isSingleSelect = input.questionType === "singleSelect";
  const isDisabled = !!responded || disabled;

  const handleToggleOption = (optionTitle: string) => {
    setSelectedOptions((prev) => {
      if (isSingleSelect) {
        return prev.includes(optionTitle) ? [] : [optionTitle];
      }
      return prev.includes(optionTitle)
        ? prev.filter((o) => o !== optionTitle)
        : [...prev, optionTitle];
    });
    if (optionTitle !== OTHER_OPTION_ID) {
      setOtherText("");
    }
  };

  const handleConfirm = () => {
    const response: { selectedOptions?: string[]; otherText?: string } = {};
    const realSelections = selectedOptions.filter((o) => o !== OTHER_OPTION_ID);
    if (realSelections.length > 0) {
      response.selectedOptions = realSelections;
    }
    if (isOtherSelected && otherText.trim()) {
      response.otherText = otherText.trim();
    }
    onRespond(toolCallId, response);
  };

  const hasSelection =
    selectedOptions.some((o) => o !== OTHER_OPTION_ID) ||
    (isOtherSelected && otherText.trim().length > 0);

  const radioOptions: RadioGroupOption<string>[] = useMemo(() => {
    const options: RadioGroupOption<string>[] = input.answerOptions.map(
      (opt) => ({
        id: opt.optionTitle,
        label: opt.optionTitle,
        labelBadge: opt.recommended ? recommendedBadge : undefined,
        description: opt.optionDescription ?? undefined,
      }),
    );
    options.push({
      id: OTHER_OPTION_ID,
      label: "Other",
      description: "Tell us how you want this done",
    });
    return options;
  }, [input.answerOptions]);

  return (
    <Flex dir="column" gap="md">
      {input.questionContext && (
        <StreamingText content={input.questionContext} isStreaming={false} />
      )}
      <div className={styles.questionBox}>
        <Flex dir="column" gap="lg">
          <StreamingText content={input.question} isStreaming={false} />
          {isSingleSelect ? (
            <Flex dir="column" gap="none">
              <RedoRadioGroup
                disabled={isDisabled}
                options={radioOptions}
                setValue={(value) => {
                  setSelectedOptions([value]);
                  if (value !== OTHER_OPTION_ID) {
                    setOtherText("");
                  }
                }}
                size="sm"
                value={selectedOptions[0] ?? ""}
              />
              {isOtherSelected && (
                <Flex pl="xl" pt="xs">
                  <RedoTextInput
                    fullWidth
                    placeholder="Add a prompt here"
                    setValue={setOtherText}
                    size="sm"
                    state={isDisabled ? "disabled" : undefined}
                    value={otherText}
                  />
                </Flex>
              )}
            </Flex>
          ) : (
            <Flex dir="column" gap="sm">
              {input.answerOptions.map((opt) => (
                <RedoCheckbox
                  description={opt.optionDescription ?? undefined}
                  disabled={isDisabled}
                  key={opt.optionTitle}
                  label={opt.optionTitle}
                  labelBadge={opt.recommended ? recommendedBadge : undefined}
                  setValue={() => handleToggleOption(opt.optionTitle)}
                  size="sm"
                  value={selectedOptions.includes(opt.optionTitle)}
                />
              ))}
              <RedoCheckbox
                description="Tell us how you want this done"
                disabled={isDisabled}
                label="Other"
                setValue={() => handleToggleOption(OTHER_OPTION_ID)}
                size="sm"
                value={isOtherSelected}
              />
              {isOtherSelected && (
                <Flex pl="xl">
                  <RedoTextInput
                    fullWidth
                    placeholder="Add a prompt here"
                    setValue={setOtherText}
                    size="sm"
                    state={isDisabled ? "disabled" : undefined}
                    value={otherText}
                  />
                </Flex>
              )}
            </Flex>
          )}
          {!responded && (
            <Flex justify="flex-start">
              <RedoButton
                disabled={!hasSelection || isDisabled}
                hierarchy="primary"
                onClick={handleConfirm}
                size="xs"
                text="Confirm"
              />
            </Flex>
          )}
        </Flex>
      </div>
    </Flex>
  );
});
